import React, { useState, useEffect } from "react";
import { ActionHandlerFunction, PaginationItem } from "@preveil-api";
import { Account, useAppSelector, DriveUIActionTypes, DirectoryEntity, Feature, DriveFeature, CollectionEntity, isSameIdentifier, DriveEntryType } from "src/common";
import { Icon, Pagination, PaginationDisplay, ToolbarButton, ToolbarWrapper } from "src/components";
import { BulkWatch } from "../entries";
import { RootState } from "src/store/configureStore";

type AllProps = {
  current_account: Account;
  is_loading: boolean;
  is_root: boolean;
  pagination?: PaginationItem;
  selected: string[];
  current_directory?: DirectoryEntity;
  collection_info?: CollectionEntity;
  handleAction: ActionHandlerFunction;
}

function ToolbarComponent(props: AllProps) {
  const { current_account, is_loading, is_root, pagination, selected, current_directory, collection_info, handleAction } = props;
  const node_permissions = collection_info?.permissions;
  const current_link = useAppSelector((state: RootState) => state.drive.current_link);
  const [btn_disabled, setButtonDisabled] = useState<boolean>(true);
  const [download_btn_disabled, setDownloadButtonDisabled] = useState<boolean>(true);

  useEffect(() => {
    (!!pagination && pagination.totalRows > 0) ?
      setButtonDisabled(selected.length <= 0) : setButtonDisabled(true);

    // NOTES: Handles Download button
    // Download will only be true if at least 1 file is selected
    if (!!current_directory) {
      const is_file_selected = current_directory.entries.some(e => (e.type === DriveEntryType.FILE) && selected.includes(e.id));
      setDownloadButtonDisabled(!is_file_selected);
    }
  }, [selected, pagination]);

  // Description: wait for the correct collection_info and current_link information
  function isDetailsReady(): boolean {
    let is_ready = false;
    if (isSameIdentifier(collection_info?.id, current_directory?.id)) {
      is_ready = (!!collection_info && (collection_info.is_maintainer && collection_info.is_root)) ?
        !!current_link : isSameIdentifier(collection_info?.collection_id, current_directory?.collection_id);
    }
    return is_ready;
  }

  return !!current_directory ? <ToolbarWrapper
    pagination={!!pagination ? <Pagination pagination={pagination} handleAction={handleAction} /> : <></>}
    paginationInfo={!!pagination ? <PaginationDisplay pagination={pagination} /> : <></>}
    refreshAction={() => handleAction({ actionType: DriveUIActionTypes.Refresh })}
    isLoading={is_loading}
  >
    {
      !!node_permissions ? <>
        {/* Multi select Bulk level actions */}
        <ToolbarButton
          hidden={Feature.hidden(DriveFeature.Download, node_permissions)}
          disabled={download_btn_disabled}
          onClick={() => handleAction({ actionType: DriveUIActionTypes.Download })}
          ariaLabel="Download Files"
          tooltipContent="Download Files">
          <Icon className="ficon-download" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => handleAction({ actionType: DriveUIActionTypes.SetStarState })}
          disabled={btn_disabled}
          ariaLabel="Add Starred Status"
          tooltipContent="Add Starred Status">
          <Icon className="ficon-star" />
        </ToolbarButton>
        <ToolbarButton
          disabled={btn_disabled}
          hidden={Feature.hidden(DriveFeature.Move, node_permissions)}
          onClick={() => handleAction({ actionType: DriveUIActionTypes.MoveModal, params: { show: true } })}
          ariaLabel="Move Selected"
          tooltipContent="Move Selected Items">
          <Icon className="pv-icon-move" />
        </ToolbarButton>
        <ToolbarButton
          disabled={btn_disabled}
          hidden={Feature.hidden(DriveFeature.Delete, node_permissions)}
          onClick={() => handleAction({ actionType: DriveUIActionTypes.Delete })}
          ariaLabel="Delete Selected"
          tooltipContent="Delete Selected Items">
          <Icon className="ficon-trash-2" />
        </ToolbarButton>
        <BulkWatch
          current_account={current_account}
          selected={selected}
          current_directory={current_directory}
          disabled={btn_disabled}
          handleAction={handleAction} />
        {/* Separator */}
        <span className="divider"></span>
        {/* Folder level actions */}
        <ToolbarButton
          hidden={Feature.hidden(DriveFeature.Upload, node_permissions)}
          disabled={is_loading}
          onClick={() => handleAction({ actionType: DriveUIActionTypes.SelectFiles })}
          ariaLabel="Upload Files"
          tooltipContent="Upload Files">
          <Icon className="ficon-upload" />
        </ToolbarButton>
        <ToolbarButton
          hidden={Feature.hidden(DriveFeature.CreateFolder, node_permissions)}
          disabled={is_loading}
          onClick={() => handleAction({ actionType: DriveUIActionTypes.CreateFolder })}
          ariaLabel="Create New Folder"
          tooltipContent="Create New Folder">
          <Icon className="ficon-folder-plus" />
        </ToolbarButton>
        <ToolbarButton
          hidden={is_root || Feature.hidden(DriveFeature.Details, node_permissions)}
          disabled={is_loading || !isDetailsReady()}
          onClick={() => handleAction({ actionType: DriveUIActionTypes.Details, params: current_directory.id })}
          ariaLabel="Current Folder Details"
          tooltipContent="Current Folder Details">
          <Icon className="pv-icon-finder" />
        </ToolbarButton>
      </> : <></>
    }
  </ToolbarWrapper> : <></>;
}

export default React.memo(ToolbarComponent);